/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { EpisodeInfo, GroupingType, SeasonInfo } from 'client-code/episode-guide';
import { PlayableVodItem, VodItemKernelType } from 'client-code/vod-browse';

import { Locale, LocaleKeys } from '../../../core/locale';

// Types
interface SeasonGroup {
    seasonTitle: string;
    seasonNumber: number;
    episodes: EpisodeInfo[];
    isHeader: true;
    showPicker: boolean;
    pickerType: 'header' | 'picker';
    seasonGroupingType: GroupingType;
    pickerItems: { listItemIndex: number; text: string }[];
}

type ListItem = EpisodeInfo | SeasonGroup | any;

class AllEpisodesListUtility {
    // Constants
    public static readonly EXTRA_SEASON_VALUE = -1;

    public static readonly MAX_ITEM_IN_PAGE_ALL_EPISODES = 16;

    public static readonly SEASON_HEADER_SIZE_ALL_EPISODES = 1;

    public static readonly EXTRA_SEASON_TEXT_VALUE = '+';

    public static getSeasonKey(seasonNumber: number, groupingType: GroupingType): string {
        return seasonNumber && seasonNumber <= AllEpisodesListUtility.EXTRA_SEASON_VALUE
            ? Locale.getString(LocaleKeys.EXTRA)
            : `${Locale.getString(
                  groupingType === GroupingType.Season ? LocaleKeys.SEASON : LocaleKeys.YEAR,
              )} ${seasonNumber}`;
    }

    public static sortSeasons(a: SeasonGroup, b: SeasonGroup): number {
        if (a.seasonTitle === `${Locale.getString(LocaleKeys.EXTRA)}`) return 1;
        if (b.seasonTitle === `${Locale.getString(LocaleKeys.EXTRA)}`) return -1;
        return a.seasonNumber - b.seasonNumber;
    }

    public static sortEpisodes(a: EpisodeInfo, b: EpisodeInfo): number {
        return (a.episodeNum || 0) - (b.episodeNum || 0);
    }

    public static isSeasonHeader(item: ListItem): item is SeasonGroup {
        return 'isHeader' in item && item.isHeader === true;
    }

    public static formatEpisodeNumber(season?: number, episode?: number): string {
        const seasonStr = season ? `S${season}` : '';
        const episodeStr = episode ? `E${episode}` : '';
        return `${seasonStr} ${episodeStr}`.trim();
    }

    public static createPlayableVodItemFromEpisode(episode: EpisodeInfo): PlayableVodItem {
        return {
            digObjectId: episode.contentId || '',
            vodItemKernel: {
                vodItemKernelType: VodItemKernelType.ContentVodItemKernel,
                contentId: episode.contentId || '',
                title: episode.title || '',
                // Map episode properties to kernel properties
                ...(episode as any), // Spread episode properties for compatibility
            },
        } as unknown as PlayableVodItem;
    }

    public static groupEpisodesBySeason(
        episodesList: EpisodeInfo[],
        seasonData: any,
        computePickerItemsFn: (seasonNumber: number) => { listItemIndex: number; text: string }[],
    ): SeasonGroup[] {
        const seasonDataMap = new Map<number, SeasonInfo>();
        const seasonsArray = seasonData?.seasonData?.seasons;
        if (seasonsArray) {
            seasonsArray.forEach((season: SeasonInfo) => {
                seasonDataMap.set(season?.seasonOrYear, season);
            });
        }

        const grouped = episodesList.reduce(
            (acc, episode) => {
                let seasonNumber = episode?.seasonOrYear;
                if (seasonNumber === undefined) {
                    seasonNumber = AllEpisodesListUtility.EXTRA_SEASON_VALUE;
                } else if (seasonsArray) {
                    const seasonInfo = seasonDataMap.get(seasonNumber);

                    if (
                        seasonInfo &&
                        seasonInfo.seasonOrYear === AllEpisodesListUtility.EXTRA_SEASON_VALUE
                    ) {
                        seasonNumber = AllEpisodesListUtility.EXTRA_SEASON_VALUE;
                    } else if (!seasonInfo) {
                        const extraSeason = seasonDataMap.get(
                            AllEpisodesListUtility.EXTRA_SEASON_VALUE,
                        );
                        if (extraSeason && extraSeason.episodeCount > 0) {
                            seasonNumber = AllEpisodesListUtility.EXTRA_SEASON_VALUE;
                        }
                    }
                }

                const seasonKey = AllEpisodesListUtility.getSeasonKey(
                    seasonNumber,
                    seasonData?.seasonGroupingType || 'season',
                );

                if (!acc[seasonKey]) {
                    acc[seasonKey] = {
                        seasonTitle: seasonKey,
                        seasonNumber,
                        episodes: [],
                        isHeader: true as const,
                        showPicker: !!seasonData?.showSeasonPicker,
                        pickerType: seasonData?.pickerType || 'header',
                        seasonGroupingType: seasonData?.seasonGroupingType || 'season',
                        pickerItems: computePickerItemsFn(seasonNumber),
                    };
                }

                acc[seasonKey].episodes.push(episode);
                return acc;
            },
            {} as Record<string, SeasonGroup>,
        );

        return Object.values(grouped).sort(AllEpisodesListUtility.sortSeasons);
    }

    public static buildFlattenedListWithHeaders(
        seasonsList: SeasonGroup[],
        maxHeaders: number,
        skipFirstHeader: boolean = false,
    ): ListItem[] {
        let result: ListItem[] = [];
        let remainingHeaders = maxHeaders;

        // Process seasons in reverse order to build list from oldest to newest
        for (let i = seasonsList.length - 1; i >= 0; i -= 1) {
            const season = seasonsList[i];
            const sortedEpisodes = season.episodes.sort(AllEpisodesListUtility.sortEpisodes);
            const isCurrentFirst = i === 0;

            // Add episodes to the beginning of result array
            result = [...sortedEpisodes, ...result];

            // Skip the first season header if it was already shown on a previous page
            const shouldSkip = isCurrentFirst && skipFirstHeader;

            // Add season header if budget allows and not a duplicate
            if (remainingHeaders > 0 && !shouldSkip) {
                result = [season, ...result];
                remainingHeaders -= AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;
            }
        }

        return result;
    }

    /**
     * Determines whether the first season on the given page is a continuation
     * from a previous page (i.e., its header was already displayed).
     *
     * @param page - Current page number
     * @param seasons - SeasonInfo array from season data
     * @returns true if the first season on this page already had its header shown on a prior page
     */
    public static isFirstSeasonContinuedFromPreviousPage(
        page: number,
        seasons: SeasonInfo[],
    ): boolean {
        if (!page || page <= 0 || !seasons?.length) return false;

        let space = page * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasons) {
            if (space < AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES) return false;
            space -= AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;
            if (space < season.episodeCount) return true;
            space -= season.episodeCount;
        }

        return false;
    }

    /**
     * Converts a page number to the corresponding episode offset in the pure episode list.
     * Uses allEpisodes constants (17 items per page, 1-line headers).
     *
     * @param page - Page number in the composite list
     * @param seasons - SeasonInfo array
     * @returns Episode offset for the start of the page, or -1 if out of bounds
     */
    public static getAllEpisodesOffsetFromPage(page: number, seasons: SeasonInfo[]): number {
        let space = page * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
        let episodeIndex = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasons) {
            // season header
            if (space < AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES) return episodeIndex;
            space -= AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;

            // season episodes
            if (space < season.episodeCount) return episodeIndex + space;
            space -= season.episodeCount;
            episodeIndex += season.episodeCount;
        }

        return -1; // (Out of bound)
    }

    /**
     * Calculates the page number and position within that page for a given episode index.
     * Uses allEpisodes constants (17 items per page, 1-line headers).
     *
     * @param episodeIndex - Index of the episode in the pure episode list
     * @param seasons - SeasonInfo array
     * @returns Page number and position (space calibrated for season headers) within the page
     */
    public static getAllEpisodesPage(
        episodeIndex: number,
        seasons: SeasonInfo[],
    ): { page: number; pageSpaceIndex: number } {
        let page = 0;
        let usedSpace = 0;
        let episodesSoFar = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasons) {
            // Season header processing
            if (
                usedSpace + AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES >
                AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES
            ) {
                page += 1;
                usedSpace = 0;
            }
            usedSpace += AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;

            // Episode belong to this season?
            if (episodeIndex < episodesSoFar + season.episodeCount) {
                const totalSpaceNeeded = usedSpace + episodeIndex - episodesSoFar;
                // Page jump
                page += Math.floor(
                    totalSpaceNeeded / AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES,
                );
                usedSpace = totalSpaceNeeded % AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
                return { page, pageSpaceIndex: usedSpace };
            }

            // Page jump
            page += Math.floor(
                (usedSpace + season.episodeCount) /
                    AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES,
            );
            usedSpace =
                (usedSpace + season.episodeCount) %
                AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;
            episodesSoFar += season.episodeCount;
        }

        return { page, pageSpaceIndex: usedSpace };
    }

    /**
     * Converts a page number and position to the corresponding index within that page.
     * Uses allEpisodes constants (17 items per page, 1-line headers).
     *
     * @param pageNo - Page number in the composite list
     * @param pageSpaceIndex - Space Position within the page (calibrated for season headers)
     * @param seasons - Array of season information with episode counts
     * @returns Index within the specified page, or -1 if out of bounds
     */
    public static getAllEpisodesPageIndex(
        pageNo: number,
        pageSpaceIndex: number,
        seasons: SeasonInfo[],
    ): number {
        const targetSpace =
            pageNo * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES + pageSpaceIndex;
        const pageStart = pageNo * AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES;

        let spaceCursor = 0;
        let pageIndex = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasons) {
            // Season header
            if (spaceCursor >= pageStart && spaceCursor < targetSpace) pageIndex += 1;
            if (
                targetSpace <
                spaceCursor + AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES
            ) {
                return pageIndex;
            }
            spaceCursor += AllEpisodesListUtility.SEASON_HEADER_SIZE_ALL_EPISODES;

            // Episode processing
            const epStart = spaceCursor;
            const epEnd = spaceCursor + season.episodeCount;
            if (targetSpace < epEnd) return pageIndex + (targetSpace - epStart);
            if (epEnd > pageStart) pageIndex += Math.max(0, epEnd - Math.max(epStart, pageStart));
            spaceCursor = epEnd;
        }

        return -1; // (Out of bound)
    }
}

export { AllEpisodesListUtility };
export type { SeasonGroup, ListItem };
